import { type ComponentType } from 'react';
import { matchRoutes, type MatchedRoute } from 'react-router-config';
import { EMPTY_OBJECT } from '@is24at/react-constants';
import {
  type AsyncConnectContext,
  type AsyncItem,
  type AsyncItemContext,
  type ResolvedAsyncItem,
} from './types';
import { endGlobalLoad } from './store';

type AsyncComponent = ComponentType & { reduxAsyncConnect: AsyncItem };

export const filterAndTransformAsyncComponents = (
  matchedRoutes: ReturnType<typeof matchRoutes>,
) => {
  const result: [AsyncComponent, MatchedRoute<any>][] = [];

  matchedRoutes.forEach(({ route, match }) => {
    const maybeAsyncConnectComponent = route?.component as AsyncComponent;

    if (maybeAsyncConnectComponent?.reduxAsyncConnect) {
      result.push([maybeAsyncConnectComponent, { route, match }]);
    }
  });

  return result;
};

export const fetchAsyncProps = async ({
  location,
  routes = [],
  ...rest
}: AsyncConnectContext) => {
  const matchedRoutes = matchRoutes(routes, location.pathname);
  const tuplesOfAsyncComponents =
    filterAndTransformAsyncComponents(matchedRoutes);

  if (!tuplesOfAsyncComponents.length) {
    return EMPTY_OBJECT as unknown as ResolvedAsyncItem;
  }

  const [component, routeParams] = tuplesOfAsyncComponents[0];
  const { key, promise } = component.reduxAsyncConnect;

  return {
    [key]: await promise({
      ...rest,
      ...routeParams,
      location,
      routes,
    } satisfies AsyncItemContext),
  } satisfies ResolvedAsyncItem;
};

export const loadOnServer = async (context: AsyncConnectContext) => {
  await fetchAsyncProps(context);
  context.store.dispatch(endGlobalLoad());
};
